import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiRequest, apiRoutes } from '../../../api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

const parcelFeatureNames = [
  'zonning',
  'in_depricated_area',
  'street_width',
  'zone_desc',
  'max_far',
  'max_n_floor',
  'max_builtarea_ratio'
];

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getProjectPoint(project, projectForm = {}) {
  const formLat = Number(projectForm.latitude);
  const formLon = Number(projectForm.longitude);
  if (Number.isFinite(formLat) && Number.isFinite(formLon)) {
    return { lat: formLat, lon: formLon };
  }

  const coordinates = project?.location?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return { lat: null, lon: null };
  }
  const savedLat = Number(coordinates[1]);
  const savedLon = Number(coordinates[0]);
  return Number.isFinite(savedLat) && Number.isFinite(savedLon)
    ? { lat: savedLat, lon: savedLon }
    : { lat: null, lon: null };
}

function formatValue(result) {
  if (result.value === -1) return 'یافت نشد';
  if (result.feature_type === 'boolian' || result.name === 'in_depricated_area') {
    return result.is_deprecated || result.value === 1 ? 'بله' : 'خیر';
  }
  if (result.zone_value) return result.zone_value;
  if (result.zone_desc_value) return result.zone_desc_value;
  if (result.value == null || result.value === '') return 'نامشخص';
  return `${Math.round(Number(result.value) * 100) / 100}${result.unit ? ` ${result.unit}` : ''}`;
}

function categoryLabel(type) {
  const labels = {
    count: 'تعداد امکانات',
    nearest_distance: 'فاصله تا نزدیک ترین',
    score: 'امتیازهای مکانی',
    urban: 'ضوابط و مقررات شهرسازی',
    other: 'سایر ویژگی ها'
  };
  return labels[type] || labels.other;
}

function buildParcelResults(parcelData, geoFeatures) {
  if (!parcelData?.found) return {};
  const results = {};
  const byName = Object.fromEntries(geoFeatures.map((feature) => [feature.name, feature]));

  const definitions = {
    zonning: {
      value: 1,
      unit: 'زون',
      raw_data: { zonning: parcelData.zonning, parcel_id: parcelData.parcel_id },
      notes: `زون: ${parcelData.zonning || 'نامشخص'}`,
      zone_value: parcelData.zonning
    },
    in_depricated_area: {
      value: parcelData.in_depricated_area ? 1 : 0,
      unit: 'وضعیت',
      raw_data: { in_depricated_area: parcelData.in_depricated_area, parcel_id: parcelData.parcel_id },
      notes: `بافت فرسوده: ${parcelData.in_depricated_area ? 'بله' : 'خیر'}`,
      is_deprecated: parcelData.in_depricated_area
    },
    street_width: {
      value: parseFloat(parcelData.street_width) || 0,
      unit: 'متر',
      raw_data: { street_width: parcelData.street_width, parcel_id: parcelData.parcel_id },
      notes: `عرض معبر: ${parcelData.street_width || 'نامشخص'} متر`
    },
    zone_desc: {
      value: parcelData.zone_desc ? 1 : 0,
      unit: 'توضیحات',
      raw_data: { zone_desc: parcelData.zone_desc, parcel_id: parcelData.parcel_id },
      notes: `توضیحات زون: ${parcelData.zone_desc || 'نامشخص'}`,
      zone_desc_value: parcelData.zone_desc
    },
    max_far: {
      value: parseFloat(parcelData.max_far) || 0,
      unit: 'ضریب',
      raw_data: { max_far: parcelData.max_far, parcel_id: parcelData.parcel_id },
      notes: `حداکثر ضریب کف فروشی: ${parcelData.max_far || 'نامشخص'}`
    },
    max_n_floor: {
      value: parseInt(parcelData.max_n_floor, 10) || 0,
      unit: 'طبقه',
      raw_data: { max_n_floor: parcelData.max_n_floor, parcel_id: parcelData.parcel_id },
      notes: `حداکثر تعداد طبقات: ${parcelData.max_n_floor || 'نامشخص'}`
    },
    max_builtarea_ratio: {
      value: parseFloat(parcelData.max_builtarea_ratio) || 0,
      unit: 'درصد',
      raw_data: { max_builtarea_ratio: parcelData.max_builtarea_ratio, parcel_id: parcelData.parcel_id },
      notes: `حداکثر درصد اشغال: ${parcelData.max_builtarea_ratio || 'نامشخص'}%`
    }
  };

  Object.entries(definitions).forEach(([name, data]) => {
    const feature = byName[name];
    if (!feature) return;
    results[name] = {
      geo_feature_id: feature.id,
      score: null,
      description: feature.description || feature.name,
      feature_type: feature.feature_type,
      name: feature.name,
      ...data
    };
  });

  return results;
}

const ProjectGeospatialStep = forwardRef(function ProjectGeospatialStep({ busy, projectForm, projectId, selectedProject, onSaved }, ref) {
  const [geoFeatures, setGeoFeatures] = useState([]);
  const [analysisResults, setAnalysisResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const autoCalculatedFor = useRef('');

  const { lat, lon } = getProjectPoint(selectedProject, projectForm);
  const activeProjectId = selectedProject?.id || projectId;

  const allPOIs = useMemo(() => {
    const pois = [];
    Object.values(analysisResults).forEach((result) => {
      const rows = result?.raw_data?.results;
      if (!Array.isArray(rows)) return;
      rows.forEach((poi) => {
        if (Array.isArray(poi.location) && poi.location.length === 2) {
          pois.push(poi);
        }
      });
    });
    return pois;
  }, [analysisResults]);

  const groupedResults = useMemo(() => {
    const grouped = {
      count: [],
      nearest_distance: [],
      score: [],
      urban: [],
      other: []
    };

    Object.entries(analysisResults).forEach(([key, result]) => {
      const row = { key, ...result };
      if (result.feature_type === 'count') {
        grouped.count.push(row);
      } else if (result.feature_type === 'nearest_distance') {
        grouped.nearest_distance.push(row);
      } else if (result.feature_type === 'score') {
        grouped.score.push(row);
      } else if (['custom', 'boolian', 'categorical', 'numerical'].includes(result.feature_type) || parcelFeatureNames.includes(result.name || key)) {
        grouped.urban.push(row);
      } else {
        grouped.other.push(row);
      }
    });

    return grouped;
  }, [analysisResults]);

  useEffect(() => {
    const loadFeatures = async () => {
      const response = await apiRequest(apiRoutes.geo.features, {
        params: { feature_types: 'count,nearest_distance,score,custom,numerical,categorical,boolian' }
      });
      if (response.ok) {
        setGeoFeatures(normalizeList(await response.json()));
      }
    };
    loadFeatures().catch(() => setError('خطا در دریافت ویژگی های مکانی.'));
  }, []);

  useEffect(() => {
    const savedFeatures = selectedProject?.geo_features || [];
    if (!savedFeatures.length) {
      setAnalysisResults({});
      return;
    }
    setAnalysisResults(Object.fromEntries(savedFeatures.map((item) => [
      item.name || item.geo_feature,
      {
        geo_feature_id: item.geo_feature,
        value: item.value,
        score: item.score,
        raw_data: item.raw_data,
        notes: item.notes,
        feature_type: item.feature_type,
        description: item.description,
        name: item.name,
        ...(item.raw_data || {})
      }
    ])));
  }, [selectedProject?.id, selectedProject?.geo_features]);

  const calculateAnalysis = async () => {
    if (lat == null || lon == null) {
      setError('ابتدا در مرحله اطلاعات پروژه، موقعیت پروژه را روی نقشه مشخص کنید.');
      return null;
    }
    if (!geoFeatures.length) {
      setError('هیچ ویژگی مکانی برای تحلیل یافت نشد.');
      return null;
    }

    setLoading(true);
    setError('');
    setProgress(0);
    const results = {};

    try {
      let parcelLookupNotes = 'برای این موقعیت داده شهرسازی یافت نشد.';
      const parcelResponse = await apiRequest(apiRoutes.geo.parcelZoning, { params: { lat, lon } });
      if (parcelResponse.ok) {
        const parcelData = await parcelResponse.json();
        Object.assign(results, buildParcelResults(parcelData, geoFeatures));
        if (parcelData?.found) {
          parcelLookupNotes = '';
        }
      } else {
        parcelLookupNotes = `خطا در دریافت داده شهرسازی: HTTP ${parcelResponse.status}`;
      }
      geoFeatures
        .filter((feature) => parcelFeatureNames.includes(feature.name) && !results[feature.name])
        .forEach((feature) => {
          results[feature.name] = {
            geo_feature_id: feature.id,
            value: -1,
            score: null,
            unit: '',
            raw_data: { parcel_lookup: true, missing: true },
            notes: parcelLookupNotes,
            feature_type: feature.feature_type,
            description: feature.description || feature.name,
            name: feature.name
          };
        });

      for (let i = 0; i < geoFeatures.length; i += 1) {
        const feature = geoFeatures[i];
        setProgress(((i + 1) / geoFeatures.length) * 100);
        if (parcelFeatureNames.includes(feature.name) || ['score', 'custom', 'boolian', 'categorical', 'numerical'].includes(feature.feature_type)) {
          if (!results[feature.name] && ['custom', 'boolian', 'categorical', 'numerical'].includes(feature.feature_type)) {
            results[feature.name] = {
              geo_feature_id: feature.id,
              value: -1,
              score: null,
              unit: '',
              raw_data: { skipped: true, reason: 'No project calculation source configured for this feature type.' },
              notes: 'برای این ویژگی منبع محاسبه پروژه تنظیم نشده است.',
              feature_type: feature.feature_type,
              description: feature.description || feature.name,
              name: feature.name
            };
          }
          continue;
        }

        if (!feature.poi_type_ids?.length) {
          results[feature.name] = {
            geo_feature_id: feature.id,
            value: -1,
            score: null,
            unit: '',
            raw_data: { skipped: true, reason: 'poi_type_ids is empty.' },
            notes: 'نوع POI برای این ویژگی تنظیم نشده است.',
            feature_type: feature.feature_type,
            description: feature.description || feature.name,
            name: feature.name
          };
          continue;
        }

        const params = { lat, lon, type_ids: feature.poi_type_ids.join(',') };
        if (feature.radius) params.radius = feature.radius;
        if (feature.count) params.max_count = feature.count;
        const response = await apiRequest(apiRoutes.geo.poisDistance, { params });

        if (response.ok) {
          const data = await response.json();
          const nearest = data.results?.[0];
          const isCount = feature.feature_type === 'count';
          results[feature.name] = {
            geo_feature_id: feature.id,
            value: isCount ? data.query?.total_found || 0 : nearest?.distance || nearest?.distance_m || -1,
            score: null,
            unit: isCount ? 'تعداد' : 'متر',
            raw_data: data,
            notes: isCount
              ? `تعداد ${feature.name} در شعاع ${feature.radius || 1000} متری`
              : nearest ? `فاصله تا نزدیک ترین ${feature.name}: ${nearest.name || 'نامشخص'}` : `هیچ ${feature.name} در محدوده یافت نشد`,
            feature_type: feature.feature_type,
            description: feature.description || feature.name,
            name: feature.name
          };
        } else {
          results[feature.name] = {
            geo_feature_id: feature.id,
            value: -1,
            score: null,
            unit: 'خطا',
            raw_data: { error: `HTTP ${response.status}` },
            notes: `خطا در دریافت اطلاعات ${feature.name}`,
            feature_type: feature.feature_type,
            description: feature.description || feature.name,
            name: feature.name
          };
        }
      }

      const hexResponse = await apiRequest(apiRoutes.geo.hexScores, { params: { lat, lon } });
      if (hexResponse.ok) {
        const hexData = await hexResponse.json();
        if (hexData.found) {
          geoFeatures.filter((feature) => feature.feature_type === 'score').forEach((feature) => {
            const value = hexData.scores?.[feature.name];
            if (value == null) {
              results[feature.name] = {
                geo_feature_id: feature.id,
                value: -1,
                score: null,
                unit: '',
                raw_data: { hex_grid: true, missing_score: true },
                notes: 'برای این امتیاز در شبکه هگزاگونال مقدار یافت نشد.',
                feature_type: 'score',
                description: feature.description || feature.name,
                name: feature.name
              };
              return;
            }
            results[feature.name] = {
              geo_feature_id: feature.id,
              value,
              score: value,
              unit: '',
              raw_data: { hex_grid: true, raw_score: value },
              notes: 'مقدار از شبکه هگزاگونال',
              feature_type: 'score',
              description: feature.description || feature.name,
              name: feature.name
            };
          });
        }
      } else {
        geoFeatures.filter((feature) => feature.feature_type === 'score').forEach((feature) => {
          results[feature.name] = {
            geo_feature_id: feature.id,
            value: -1,
            score: null,
            unit: 'خطا',
            raw_data: { error: `HTTP ${hexResponse.status}` },
            notes: 'خطا در دریافت امتیاز شبکه هگزاگونال',
            feature_type: 'score',
            description: feature.description || feature.name,
            name: feature.name
          };
        });
      }

      geoFeatures.filter((feature) => feature.feature_type === 'score' && !results[feature.name]).forEach((feature) => {
        results[feature.name] = {
          geo_feature_id: feature.id,
          value: -1,
          score: null,
          unit: '',
          raw_data: { hex_grid: true, missing_score: true },
          notes: 'برای این امتیاز در شبکه هگزاگونال مقدار یافت نشد.',
          feature_type: 'score',
          description: feature.description || feature.name,
          name: feature.name
        };
      });

      setAnalysisResults(results);
      return results;
    } catch (err) {
      setError(`خطا در تحلیل مکانی: ${err.message}`);
      return null;
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    const key = `${selectedProject?.id || 'draft'}-${lat}-${lon}-${geoFeatures.length}`;
    if (lat == null || lon == null || !geoFeatures.length || autoCalculatedFor.current === key) return;
    if (selectedProject?.geo_features?.length) return;
    autoCalculatedFor.current = key;
    calculateAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, geoFeatures.length, selectedProject?.id]);

  const saveAnalysis = async () => {
    const results = Object.keys(analysisResults).length ? analysisResults : await calculateAnalysis();
    if (!results || !Object.keys(results).length || !activeProjectId) return false;
    setSaving(true);
    setError('');
    try {
      const response = await apiRequest(apiRoutes.geo.saveAnalysis, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: activeProjectId, analysisResults: results })
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || data.detail || 'خطا در ذخیره تحلیل مکانی.');
        return false;
      }
      await onSaved?.();
      return true;
    } catch (err) {
      setError(`خطا در ذخیره تحلیل مکانی: ${err.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ saveAnalysis }));

  return (
    <div className="dev-geo-layout">
      <div className="dev-panel dev-geo-map-panel">
        <div className="dev-geo-panel-title">
          <h2>نقشه تحلیل</h2>
          <span>{geoFeatures.length} ویژگی</span>
        </div>
        {lat != null && lon != null ? (
          <div className="dev-map-container">
            <MapContainer center={[lat, lon]} zoom={14} className="dev-map" key={`${lat}-${lon}`}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              <Marker position={[lat, lon]}>
                <Popup>{selectedProject?.name || 'موقعیت پروژه'}</Popup>
              </Marker>
              {allPOIs.map((poi, index) => (
                <Marker key={`${poi.name || 'poi'}-${index}`} position={[poi.location[0], poi.location[1]]}>
                  <Popup>{poi.name || 'بدون نام'}</Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : <p className="dev-muted">موقعیت پروژه هنوز ثبت نشده است.</p>}
      </div>

      <div className="dev-panel dev-geo-results-panel">
        <div className="dev-panel-head dev-geo-head">
          <div>
            <h2>تحلیل مکانی پروژه</h2>
            <p>دسترسی ها، امکانات اطراف، امتیازهای مکانی و داده های شهرسازی.</p>
          </div>
          <div className="dev-actions dev-geo-actions">
            <button type="button" className="dev-btn secondary" onClick={calculateAnalysis} disabled={!activeProjectId || loading || saving || busy}>
              {loading ? 'در حال تحلیل...' : 'محاسبه'}
            </button>
          </div>
        </div>
        {lat == null || lon == null ? (
          <div className="dev-alert error compact">ابتدا در مرحله اطلاعات پروژه، موقعیت پروژه را روی نقشه مشخص و ذخیره کنید.</div>
        ) : null}
        {error && <div className="dev-alert error compact">{error}</div>}
        {loading && (
          <div className="dev-progress-line">
            <span style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className="dev-geo-categories">
          {Object.entries(groupedResults).map(([type, rows]) => rows.length > 0 && (
            <section className="dev-geo-category" key={type}>
              <h3>{categoryLabel(type)} ({rows.length})</h3>
              <div className="dev-results-grid">
                {rows.map((result) => (
                  <div className="dev-result-card" key={result.key}>
                    <small>{result.description || result.name || result.key}</small>
                    <strong>{formatValue(result)}</strong>
                    <span>{result.notes}</span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
});

export default ProjectGeospatialStep;
